// ClinicalIndustry page component
import { useState } from "react";
import heroMdCroImg from "@/assets/services/clinical-solutions/hero-md-cro.png";
import heroOrthopaedicSpineAsset from "@/assets/services/clinical-solutions/hero-orthopaedic-spine.jpg.asset.json";
import qbdIvdQaradWhite from "@/assets/services/clinical-solutions/qbd-ivd-qarad-white.png";
import qbdIvdQaradLogo from "@/assets/services/clinical-solutions/qbd-ivd-qarad.png";
import { Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import SEOHead from "@/components/SEOHead";
import { useLanguage } from "@/hooks/useLanguage";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { DIVISION_LOGOS } from "@/lib/divisionLogos";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import FaqSchema from "@/components/seo/FaqSchema";
import ContactCTA from "@/components/ContactCTA";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getServiceBySlug } from "@/data/servicesData";
import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceOfferings from "@/components/services/ServiceOfferings";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import MedicalDeviceCROContent from "@/components/services/MedicalDeviceCROContent";
import IVDPerformanceContent, { ivdFaqItems } from "@/components/services/IVDPerformanceContent";
import CardiologyCROContent from "@/components/services/CardiologyCROContent";
import OrthopaedicSpineCROContent from "@/components/services/OrthopaedicSpineCROContent";
import { mdCroT } from "@/i18n/mdCro";
import { ClipboardList } from "lucide-react";

const industryPages: Record<string, { title: string; subtitle: string; filterIndustry: string; description: string; heroImage?: string; ctaTitle?: string; ctaDescription?: string }> = {
  "/en/services/full-service-medical-device-cro": {
    title: "Full-Service Medical Device CRO",
    subtitle: "QbD Clinical is a full-service CRO dedicated to medical device companies. From early feasibility and pilot studies to global clinical trials across Europe, North America, and APAC, and until PMCF studies, we accompany our sponsors in bringing safe, effective devices to patients on time and on budget.",
    filterIndustry: "Medical Devices",
    description: "",
    heroImage: heroMdCroImg,
  },
  "/en/services/clinical-solutions/ivd-clinical-performance-studies": {
    title: "In Vitro Diagnostics CRO",
    subtitle: "Specialized in IVD Clinical Performance Studies",
    filterIndustry: "In Vitro Diagnostics",
    description: "",
    ctaTitle: "Ready to advance your IVD to market?",
    ctaDescription: "From study design to final performance evaluation, our IVD specialists are ready to support your in vitro diagnostics journey to IVDR compliance.",
  },
  "/en/industries/companion-diagnostics": {
    title: "PrecisionDx™ (Companion Diagnostics)",
    subtitle: "Integrated clinical and regulatory support for CDx development",
    filterIndustry: "Companion Diagnostics",
    description: "We support CDx programs in close alignment with drug development, ensuring clinical evidence, regulatory strategy and timelines remain synchronized.",
  },
  "/en/industries/medical-devices/md-cro-cardiology": {
    title: "Your CRO for Cardiology Medical Devices",
    subtitle: "Specialized support for clinical research in cardiology",
    filterIndustry: "Medical Devices",
    description: "As a European CRO dedicated to medical devices, we offer deep expertise in Cardiology Medical Device clinical research. From heart failure devices and stents to structural heart innovations, we support your journey from strategy to post-market follow-up.",
  },
  "/en/industries/medical-devices/md-cro-orthopaedic-spine": {
    title: "Your CRO for Orthopaedic & Spine Medical Devices",
    subtitle: "Clinical evidence built for EU MDR, PMCF and market access",
    filterIndustry: "Medical Devices",
    description: "Orthopaedic and spine studies bring specific clinical and operational challenges, from surgeon and site selection to imaging, PROMs and long-term implant follow-up. QbD Group combines medical device CRO execution with regulatory and scientific expertise to build the evidence for your implant, spine or surgical technology, from strategy to post-market follow-up.",
    heroImage: heroOrthopaedicSpineAsset.url,
    ctaTitle: "Build an orthopaedic evidence strategy fit for your next milestone",
    ctaDescription: "Whether you're refining your clinical strategy or launching a pivotal orthopaedic or spine study, our clinical and regulatory experts are ready to support your MedTech journey, from gap assessment to full CRO delivery.",
  },
  "/es/industrias/medical-devices/md-cro-cardiologia": {
    title: "Tu CRO para Medical Devices de Cardiología",
    subtitle: "Soporte especializado para la investigación clínica en cardiología",
    filterIndustry: "Medical Devices",
    description: "Como CRO europea dedicada a los Medical Devices, contamos con una profunda experiencia en investigación clínica de Medical Devices de cardiología. Desde dispositivos para insuficiencia cardíaca y stents hasta innovaciones en cardiopatía estructural, te acompañamos desde la estrategia hasta el seguimiento poscomercialización.",
    ctaTitle: "Planifica tu recorrido clínico",
    ctaDescription: "Desde la evaluación de carencias hasta la entrega integral como CRO, nuestros expertos clínicos están listos para acompañarte en tu recorrido MedTech.",
  },
  /* ES equivalents */
  "/es/servicios/clinical-solutions/ensayos-clinicos-de-dispositivos-medicos": {
    title: "CRO de Medical Devices",
    subtitle: "QbD Clinical es un CRO de servicio completo dedicado a empresas de Medical Devices. Desde estudios piloto y de viabilidad hasta ensayos clínicos globales y estudios PMCF, acompañamos a nuestros promotores.",
    filterIndustry: "Medical Devices",
    description: "",
    heroImage: heroMdCroImg,
  },
  "/es/servicios/clinical-solutions/estudios-de-rendimiento-clínico-de-ivd": {
    title: "CRO de Diagnósticos In Vitro",
    subtitle: "Especializados en Estudios de Rendimiento Clínico de IVD",
    filterIndustry: "In Vitro Diagnostics",
    description: "",
    ctaTitle: "¿Listo para llevar su IVD al mercado?",
    ctaDescription: "Desde el diseño del estudio hasta la evaluación final del rendimiento, nuestros especialistas en IVD están listos para apoyar su camino hacia el cumplimiento del IVDR.",
  },
  /* ZH equivalents (path stays in English; lang detected from /zh-cn/ prefix) */
  "/zh-cn/services/full-service-medical-device-cro": {
    title: "全方位医疗器械 CRO",
    subtitle: "QbD Clinical 是一家专门服务于医疗器械公司的全方位 CRO。从早期可行性研究和先导研究,到覆盖欧洲、北美和亚太地区的全球临床试验,以及上市后临床随访 (PMCF) 研究,我们陪伴申办方按时、按预算地将安全有效的器械送达患者。",
    filterIndustry: "Medical Devices",
    description: "",
    heroImage: heroMdCroImg,
  },
  "/zh-cn/services/clinical-solutions/ivd-clinical-performance-studies": {
    title: "体外诊断 CRO",
    subtitle: "专注于 IVD 临床性能研究",
    filterIndustry: "In Vitro Diagnostics",
    description: "",
    ctaTitle: "准备好将您的 IVD 推向市场了吗?",
    ctaDescription: "从研究设计到最终性能评估,我们的 IVD 专家随时为您的体外诊断之路提供 IVDR 合规支持。",
  },
  "/zh-cn/industries/companion-diagnostics": {
    title: "PrecisionDx™(伴随诊断)",
    subtitle: "为 CDx 开发提供整合的临床和法规支持",
    filterIndustry: "Companion Diagnostics",
    description: "我们与药物开发紧密协同地支持 CDx 项目,确保临床证据、法规策略和时间线保持同步。",
  },
};

const ClinicalIndustry = () => {
  const location = useStableLocation();
  const [formOpen, setFormOpen] = useState(false);
  const lang = useLanguage();
  const m = mdCroT(lang);

  // Some Spanish URLs exist in both accented and ASCII-folded form (both routes
  // are live), so resolve the page ignoring accents.
  const foldPath = (value: string) =>
    decodeURIComponent(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const page =
    industryPages[location.pathname] ??
    Object.entries(industryPages).find(([key]) => foldPath(key) === foldPath(location.pathname))?.[1];
  const parentService = getServiceBySlug("clinical-solutions");

  if (!page || !parentService) return <Navigate to="/en/services/clinical-solutions" replace />;

  const heroService = {
    ...parentService,
    title: page.title,
    subtitle: page.subtitle,
    description: page.description,
    path: location.pathname,
    ...(page.heroImage && { heroImage: page.heroImage }),
  };

  const SITE = "https://qbdgroup.com";

  const isIvd = location.pathname === "/en/services/clinical-solutions/ivd-clinical-performance-studies" || location.pathname === "/es/servicios/clinical-solutions/estudios-de-rendimiento-clinico-de-ivd" || location.pathname === "/es/servicios/clinical-solutions/estudios-de-rendimiento-clinico-de-ivd" || location.pathname === "/zh-cn/services/clinical-solutions/ivd-clinical-performance-studies";
  const isCdx = location.pathname === "/en/industries/companion-diagnostics" || location.pathname === "/zh-cn/industries/companion-diagnostics";
  const isMdCro = location.pathname === "/en/services/full-service-medical-device-cro" || location.pathname === "/es/servicios/clinical-solutions/ensayos-clinicos-de-dispositivos-medicos" || location.pathname === "/zh-cn/services/full-service-medical-device-cro";
  const isOrthopaedicSpine = location.pathname === "/en/industries/medical-devices/md-cro-orthopaedic-spine";
  const COLOR_IVD = "#FFC60C";

  const mdCroFaqItems = [
    { question: m("faqQ1"), answer: m("faqA1") },
    { question: m("faqQ2"), answer: m("faqA2") },
    { question: m("faqQ3"), answer: m("faqA3") },
    { question: m("faqQ4"), answer: m("faqA4") },
  ];

  const activeFaqItems = isIvd ? ivdFaqItems(lang) : isMdCro ? mdCroFaqItems : null;

  return (
    <>
      <SEOHead
        title={`${page.title} | ${isIvd ? "QbD IVD | Qarad" : "QbD Group"}`}
        description={page.description.slice(0, 155)}
        canonical={`${SITE}${location.pathname}`}
        ogImage={page.heroImage || parentService.heroImage}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.title,
            description: page.description,
            provider: { "@type": "Organization", name: "QbD Group", url: SITE },
            url: `${SITE}${location.pathname}`,
            isPartOf: { "@type": "Service", name: parentService.title, url: `${SITE}${parentService.path}` },
            ...keyTakeawaySchema(heroTakeaway(page.subtitle, page.description)),
          })}
        </script>
      </SEOHead>


      <ScrollToTop />
      <Navbar logoOverride={(isIvd || isCdx)
        ? { src: qbdIvdQaradWhite, srcDark: qbdIvdQaradLogo, alt: "QbD IVD | Qarad", className: "h-16 sm:h-[4.5rem] w-auto transition-opacity duration-300" }
        : DIVISION_LOGOS["clinical-solutions"]
      } />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': parentService.color } as React.CSSProperties}>
        <ServiceHero
          service={heroService}
          onCtaClick={() => setFormOpen(true)}
          parentCrumbs={
            location.pathname === "/en/industries/medical-devices/md-cro-cardiology" || isOrthopaedicSpine
              ? [
                  { label: "Clinical Solutions", to: "/en/services/clinical-solutions" },
                  { label: "Medical Device CRO", to: "/en/services/full-service-medical-device-cro" },
                ]
              : location.pathname === "/es/industrias/medical-devices/md-cro-cardiologia"
                ? [
                    { label: "Soluciones Clínicas", to: "/es/servicios/clinical-solutions" },
                    { label: "CRO de Medical Devices", to: "/es/servicios/clinical-solutions/ensayos-clinicos-de-dispositivos-medicos" },
                  ]
                : lang === "zh-cn"
                  ? [{ label: "临床解决方案", to: "/zh-cn/services/clinical-solutions" }]
                  : [{ label: "Clinical Solutions", to: "/en/services/clinical-solutions" }]
          }
          partnerLogo={
            isIvd
              ? { src: qbdIvdQaradLogo, alt: "QbD IVD | Qarad" }
              : undefined
          }
          discoverButtonTarget={isIvd ? "ivd-services" : isOrthopaedicSpine ? "ortho-overview" : undefined}
          discoverButtonLabel={isOrthopaedicSpine ? "Discover our offerings" : undefined}
          tertiaryCta={
            location.pathname === "/en/industries/medical-devices/md-cro-cardiology"
              ? {
                  label: "Check your trial readiness",
                  href: "https://qbdgroup.com/en/eu-readiness-cardiology",
                  className: "bg-[#63A8FC] text-white hover:bg-[#63A8FC]/90",
                  icon: ClipboardList,
                }
              : location.pathname === "/es/industrias/medical-devices/md-cro-cardiologia"
                ? {
                    label: "Comprueba tu preparación",
                    href: "https://qbdgroup.com/es/eu-readiness-cardiology",
                    className: "bg-[#63A8FC] text-white hover:bg-[#63A8FC]/90",
                    icon: ClipboardList,
                  }
                : undefined
          }
        />
        {isOrthopaedicSpine ? (
          <OrthopaedicSpineCROContent onCtaClick={() => setFormOpen(true)} />
        ) : isMdCro ? (
          <MedicalDeviceCROContent
            onCtaClick={() => setFormOpen(true)}
            offeringsSlot={<ServiceOfferings serviceSlug="clinical" filterIndustry={page.filterIndustry} color={parentService.color} defaultLayout="list" sectionTitle={m("featuredOfferings")} extraLinks={[{ title: m("legalRepTitle"), href: "/en/services/regulatory-affairs/legal-representative", description: m("legalRepDesc"), image_url: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/regulatory-affairs/legal-representative.jpg" }]} />}
          />
        ) : isIvd ? (
          <IVDPerformanceContent
            onCtaClick={() => setFormOpen(true)}
            offeringsSlot={<ServiceOfferings serviceSlug="clinical" filterIndustry={page.filterIndustry} color={parentService.color} defaultLayout="list" sectionTitle="Featured offerings" excludeIds={["1ec1a0bd-56d7-42d4-a827-f4f345d9f5be"]} extraLinks={[{ title: "Legal Representative", href: "/en/services/regulatory-affairs/legal-representative", description: "If the sponsor of a performance study is not established in the European Union, we can ensure that a natural or legal person is established in the Union as its legal representative.", image_url: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/regulatory-affairs/legal-representative.jpg" }]} />}
          />
        ) : location.pathname === "/en/industries/medical-devices/md-cro-cardiology" || location.pathname === "/es/industrias/medical-devices/md-cro-cardiologia" ? (
          <CardiologyCROContent
            onCtaClick={() => setFormOpen(true)}
            offeringsSlot={<ServiceOfferings serviceSlug="clinical" filterIndustry={page.filterIndustry} color={parentService.color} defaultLayout="list" sectionTitle={lang === "es" ? "Servicios destacados" : "Featured offerings"} />}
          />
        ) : (
          <ServiceOfferings serviceSlug="clinical-solutions" filterIndustry={page.filterIndustry} color={parentService.color} defaultLayout="list" />
        )}
        {location.pathname !== "/en/industries/medical-devices/md-cro-cardiology" && location.pathname !== "/es/industrias/medical-devices/md-cro-cardiologia" && !isOrthopaedicSpine && (
          <ServiceWhyQbd
            service={parentService}
            onCtaClick={() => setFormOpen(true)}
            sectionLogo={isIvd ? qbdIvdQaradWhite : undefined}
            brandLabel={isIvd ? "Why QbD IVD | Qarad?" : undefined}
          />
        )}

        {/* FAQ – rendered after Why QbD */}
        {activeFaqItems && (
          <section id="faq" className="py-16 sm:py-20 bg-muted/30 scroll-mt-28">
            <FaqSchema items={activeFaqItems} />
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR_IVD }} />
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{({ en: "MedTech study requirements", es: "Requisitos de estudios MedTech", "zh-cn": "MedTech研究证据要求" }[lang])}</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-8">{m("faqTitle")}</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {activeFaqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-xl px-6">
                    <AccordionTrigger className="text-base font-medium text-foreground hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        <ServiceRelatedContent
          service={parentService}
          contentTags={isIvd ? ["Clinical", "IVD"] : ["Clinical", page.filterIndustry]}
          curatedSlugs={isIvd ? [
            { type: "blog" as const, slug: "ivdr-compliance-large-panel-ngs" },
            { type: "blog" as const, slug: "ivdr-global-regulatory-strategy" },
            { type: "blog" as const, slug: "what-makes-usability-testing-crucial-for-near-patient-and-self-testing-devices-under-ivdr" },
            { type: "blog" as const, slug: "how-to-define-your-clinical-performance-strategy" },
            { type: "white-paper" as const, slug: "clinical-evidence-for-in-vitro-diagnostics-under-ivdr" },
            { type: "webinar" as const, slug: "mastering-clinical-performance-studies-under-ivdr-from-strategy-to-delivery" },
          ] : undefined}
          sectionTitle={isIvd ? "Resources" : undefined}
        />

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{parentService.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={parentService.title}
              hubspotFormId={parentService.hubspotFormId}
              heading={parentService.formHeading}
              subtitle={isIvd ? "From study design to final performance evaluation, our IVD specialists are ready to support your Medical Device or IVD journey." : parentService.formSubtitle}
            />
          </DialogContent>
        </Dialog>

        <ContactCTA
          onContactClick={() => setFormOpen(true)}
          title={page.ctaTitle || parentService.formHeading}
          description={page.ctaDescription || parentService.formSubtitle}
        />
      </main>

      <Footer />
    </>
  );
};

export default ClinicalIndustry;
